// Laser remnant / stock-drop label confirmation (2026-09-23).
//
// Remnants were leaving the laser unlabeled with "the label never printed" as the
// explanation. Every print job that puts a remnant or drop label on the ZP 505 now asks
// the operator, in this popup, whether the label printed. It cannot be dismissed without
// an answer (no close button, no backdrop click). "Didn't print" makes jobq-api post the
// label PDF to shop alerts so the office can reprint it, beside what the print agent
// itself reported; if the agent says it printed, the operator is asked to look in the
// tray and on the floor first. Answers are stored with the print job
// (POST /print/queue/{id}/confirm_label).
import React, { useEffect, useRef, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from "../utils/apiError";
import {
  answerOnRecord, clockText, confirmationTitle, isPrintSettled, labelSummaryText,
  needsDoubleCheck, officeOutcome, pieceText, printStatusLine,
} from "../utils/labelConfirm";

const API_BASE = config.API_BASE_URL;
const POLL_MS = 2000;
const MAX_POLLS = 150;   // ~5 minutes of live status; the answer never depends on it

// Unanswered label questions on a task, oldest first. Fails OPEN ([]) on any error: a
// backend hiccup must never trap the laser station, and Complete reads this.
export async function loadPendingLabelConfirmations(taskId) {
  if (!taskId) return [];
  try {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/label_confirmations?pending_only=true`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.confirmations) ? data.confirmations : [];
  } catch (_) {
    return [];
  }
}

async function fetchPrintJob(printJobId) {
  const res = await fetch(`${API_BASE}/print/queue/${printJobId}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const TONE = {
  ok: { color: "#0f5132", background: "#d1e7dd", border: "#a3cfbb" },
  warn: { color: "#664d03", background: "#fff3cd", border: "#ffe69c" },
  error: { color: "#842029", background: "#f8d7da", border: "#f1aeb5" },
  waiting: { color: "var(--jq-text-muted, #495057)", background: "var(--jq-bg-subtle, #e9ecef)", border: "var(--jq-border-medium, #ced4da)" },
};

const bigButton = (background, color = "#fff") => ({
  flex: 1, minHeight: "56px", padding: "0.6rem 0.9rem", fontSize: "1.05rem", fontWeight: "bold",
  backgroundColor: background, color, border: "none", borderRadius: "6px", cursor: "pointer",
});

export default function RemnantLabelConfirmModal({ confirmation, intro = null, onClose }) {
  const printJobId = confirmation && confirmation.print_job_id;
  // A pending-list entry already carries the job's status; Add Sheet's answer does not.
  const [job, setJob] = useState(confirmation && confirmation.status ? confirmation : null);
  const [step, setStep] = useState("ask");   // ask | double_check | note | saving | done
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState(null);
  const [fatal, setFatal] = useState(false);
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!printJobId) return undefined;
    let alive = true;
    let timer = null;
    pollsRef.current = 0;
    const tick = async () => {
      try {
        const j = await fetchPrintJob(printJobId);
        if (!alive) return;
        setJob(j);
        if (isPrintSettled(j)) return;
      } catch (_) {
        // Keep the last status on screen; the operator can answer either way.
      }
      pollsRef.current += 1;
      if (alive && pollsRef.current < MAX_POLLS) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [printJobId]);

  if (!printJobId) return null;

  const labels = (job && job.labels) || confirmation.labels;
  const sheetIndex = job && job.sheet_index != null ? job.sheet_index : confirmation.sheet_index;
  const conf = { sheet_index: sheetIndex, labels };
  const status = printStatusLine(job);
  const tone = TONE[status.tone] || TONE.waiting;
  const busy = step === "saving";

  function finish(answer, body) {
    const recorded = (body && body.label_confirmation) || answer;
    if (recorded === "printed") {
      onClose({ answer: "printed" });
      return;
    }
    setOutcome(officeOutcome(body, conf));
    setStep("done");
  }

  async function submit(answer) {
    const back = step;
    setError(null);
    setStep("saving");
    try {
      const res = await fetch(`${API_BASE}/print/queue/${printJobId}/confirm_label`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer, note: answer === "not_printed" ? (note.trim() || null) : null }),
      });
      if (res.ok) {
        finish(answer, await res.json());
        return;
      }
      if (res.status >= 400 && res.status < 500) {
        // A refusal (unknown job, nothing to confirm) will not change on a retry.
        setError(await apiErrorMessage(res));
        setFatal(true);
        setStep(back);
        return;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (_) {
      // A request that died in transit (API Gateway's 30 s cap, Wi-Fi) may still have
      // landed: the job says whether the answer is on record.
      try {
        const rec = answerOnRecord(await fetchPrintJob(printJobId));
        if (rec.landed) {
          finish(rec.answer, { label_confirmation: rec.answer, office_alerted: rec.office_alerted });
          return;
        }
      } catch (_ignored) {
        // fall through to the retry message
      }
      setError("Your answer was not saved (connection problem). Check the connection and press it again.");
      setStep(back);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remnant-label-confirm-title"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1100, padding: "16px",
      }}
    >
      <div style={{
        backgroundColor: "var(--jq-bg-card, #fff)", color: "var(--jq-text, #212529)", padding: "20px",
        borderRadius: "8px", width: "min(520px, 100%)", boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        borderTop: "6px solid #6f42c1",
      }}>
        {intro && (
          <div style={{
            marginBottom: "10px", padding: "6px 10px", borderRadius: "6px", fontSize: "0.9rem",
            color: TONE.warn.color, backgroundColor: TONE.warn.background, border: `1px solid ${TONE.warn.border}`,
          }}>
            {intro}
          </div>
        )}
        <h4 id="remnant-label-confirm-title" style={{ marginTop: 0, marginBottom: "6px" }}>
          🏷️ {confirmationTitle(conf)}
        </h4>
        <div style={{ fontSize: "1.05rem", marginBottom: "12px" }}>{labelSummaryText(labels)}</div>

        <div
          data-testid="label-print-status"
          style={{
            padding: "8px 10px", borderRadius: "6px", marginBottom: "14px", fontSize: "0.95rem",
            color: tone.color, backgroundColor: tone.background, border: `1px solid ${tone.border}`,
          }}
        >
          {status.text}
        </div>

        {step === "ask" && (
          <>
            <p style={{ marginBottom: "6px" }}>
              Take the label from the printer now and stick it on {pieceText(labels)} as soon as the sheet is cut.
            </p>
            <p style={{ fontWeight: "bold", fontSize: "1.1rem", marginBottom: "10px" }}>Did the label print?</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" disabled={busy} style={bigButton("#198754")} onClick={() => submit("printed")}>
                ✅ Yes, it printed. I have it.
              </button>
              <button
                type="button"
                disabled={busy}
                style={bigButton("#dc3545")}
                onClick={() => setStep(needsDoubleCheck(job) ? "double_check" : "note")}
              >
                ❌ No, it didn't print
              </button>
            </div>
          </>
        )}

        {step === "double_check" && (
          <>
            <p style={{ marginBottom: "10px" }}>
              <strong>The printer says it printed this label at {clockText(job && job.printed_at)}.</strong>{" "}
              Check the printer's output and the floor around it before you send it to the office.
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" disabled={busy} style={bigButton("#198754")} onClick={() => submit("printed")}>
                ✅ Found it. It printed.
              </button>
              <button type="button" disabled={busy} style={bigButton("#dc3545")} onClick={() => setStep("note")}>
                Still missing. Tell the office.
              </button>
            </div>
          </>
        )}

        {step === "note" && (
          <>
            <p style={{ marginBottom: "8px" }}>
              The office gets this label in Slack and will print it for you.
            </p>
            <label style={{ display: "block", fontSize: "0.9rem", marginBottom: "10px" }}>
              What happened? (optional)
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g. printer out of labels, label came out blank, jammed"
                style={{ width: "100%", marginTop: "4px" }}
              />
            </label>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button type="button" disabled={busy} style={bigButton("#dc3545")} onClick={() => submit("not_printed")}>
                Send it to the office
              </button>
              <button
                type="button"
                disabled={busy}
                style={bigButton("var(--jq-bg-subtle, #e9ecef)", "var(--jq-text, #212529)")}
                onClick={() => setStep("ask")}
              >
                Back
              </button>
            </div>
          </>
        )}

        {step === "saving" && <p style={{ marginBottom: 0 }}>Saving…</p>}

        {step === "done" && outcome && (
          <>
            <div style={{
              padding: "10px 12px", borderRadius: "6px", marginBottom: "12px",
              color: TONE[outcome.tone].color, backgroundColor: TONE[outcome.tone].background,
              border: `1px solid ${TONE[outcome.tone].border}`,
            }}>
              {outcome.text}
            </div>
            <button
              type="button"
              style={bigButton("#0d6efd")}
              onClick={() => onClose({ answer: "not_printed", office_alerted: outcome.tone === "ok" })}
            >
              OK
            </button>
          </>
        )}

        {error && (
          <div style={{
            marginTop: "12px", padding: "8px 10px", borderRadius: "6px", color: TONE.error.color,
            backgroundColor: TONE.error.background, border: `1px solid ${TONE.error.border}`,
          }}>
            {error}
            {fatal && (
              <div style={{ marginTop: "8px" }}>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onClose({ answer: null })}>
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
